import type { ChipProps } from '@citibox-panel/ui';
import { capitalize } from './capitalize';

// Canonical wire spellings of the delivery `status` enum as confirmed by backend —
// underscores (non_collected / non_retrieved), lowercase on the wire. Every consumer
// (chips, trazabilidad, action eligibility, timeline) matches case-insensitively through
// these constants so each spelling lives in exactly one place. The internal pre-delivery
// states (initialized/created) are deliberately absent: they should never be served to
// the panel, and if one leaks it takes the same fail-open path as any unknown state.
export const DELIVERY_STATES = {
  stored: 'stored',
  collected: 'collected',
  expired: 'expired',
  nonCollected: 'non_collected',
  retrieved: 'retrieved',
  nonRetrieved: 'non_retrieved',
  rejected: 'rejected',
  blocked: 'blocked',
  cancelled: 'cancelled',
  failed: 'failed',
} as const;

export type DeliveryState = (typeof DELIVERY_STATES)[keyof typeof DELIVERY_STATES];

interface DeliveryStatePresentation {
  chipLabel: string;
  chipColor: NonNullable<ChipProps['color']>;
  timelineLabel: string;
}

// Chip: short CC-friendly state name («recogida» = action of the «Usuario»,
// «recuperación» = action of the courier). Timeline: event-style trazabilidad label —
// same fact, two grammatical frames, deliberately different. Colors: green = parcel live
// in the «buzón», amber = attention, red = active problem, grey = neutral/closed ending.
// TODO: i18n — copy en ES hasta Fase 1.
const STATE_PRESENTATION: Record<DeliveryState, DeliveryStatePresentation> = {
  [DELIVERY_STATES.stored]: { chipLabel: 'En el buzón', chipColor: 'success', timelineLabel: 'Entregado' },
  [DELIVERY_STATES.collected]: { chipLabel: 'Recogido', chipColor: 'default', timelineLabel: 'Recogido' },
  [DELIVERY_STATES.expired]: { chipLabel: 'Expirado', chipColor: 'warning', timelineLabel: 'Tiempo de recogida expirado' },
  [DELIVERY_STATES.nonCollected]: { chipLabel: 'Recogida vacía', chipColor: 'default', timelineLabel: 'Recogida vacía' },
  [DELIVERY_STATES.retrieved]: { chipLabel: 'Recuperado', chipColor: 'default', timelineLabel: 'Recuperado por el mensajero' },
  [DELIVERY_STATES.nonRetrieved]: { chipLabel: 'Recuperación vacía', chipColor: 'default', timelineLabel: 'Recuperación vacía' },
  [DELIVERY_STATES.rejected]: { chipLabel: 'Rechazado', chipColor: 'default', timelineLabel: 'Recogida rechazada por el usuario' },
  [DELIVERY_STATES.blocked]: { chipLabel: 'Bloqueado', chipColor: 'error', timelineLabel: 'Recogida bloqueada' },
  [DELIVERY_STATES.cancelled]: { chipLabel: 'Cancelado', chipColor: 'default', timelineLabel: 'Entrega cancelada' },
  [DELIVERY_STATES.failed]: { chipLabel: 'Fallido', chipColor: 'default', timelineLabel: 'Entrega fallida' },
};

function presentation(status: string): DeliveryStatePresentation | undefined {
  // Own-property only: `status` is wire data, and a bare lookup also resolves inherited names, so a
  // `constructor` status would hand back the Object function. Every caller here happens to survive
  // it via `?.prop ?? fallback`, but the guard belongs at the lookup — not in the luck of the
  // consumers, one `if (presentation(status))` away from breaking.
  const key = status.toLowerCase();
  return Object.hasOwn(STATE_PRESENTATION, key)
    ? STATE_PRESENTATION[key as DeliveryState]
    : undefined;
}

// CC-friendly chip label; an unmapped state fails open to the capitalized raw string so a
// new backend state still renders, never breaks the card.
export function deliveryStateChipLabel(status: string): string {
  return presentation(status)?.chipLabel ?? capitalize(status);
}

// Semantic chip colour; an unmapped state degrades to the neutral chip.
export function deliveryStateChipColor(status: string): NonNullable<ChipProps['color']> {
  return presentation(status)?.chipColor ?? 'default';
}

// Trazabilidad (event-style) label; an unmapped state fails open to the raw string in its
// original casing (the timeline never invents a reading for a state it does not know).
export function deliveryTimelineLabel(status: string): string {
  return presentation(status)?.timelineLabel ?? status;
}
